"use client";

import { cn } from "@/lib/utils";
import { motion, Transition } from "framer-motion";
export type GlowEffectProps = {
  className?: string;
  style?: React.CSSProperties;
  colors?: string[];
  mode?: "rotate" | "pulse" | "breathe" | "colorShift" | "flowHorizontal" | "static" | "colorToTransparentShift" | "colorToTransparentLeftToRight" | "colorToTransparentRightToLeft" | "colorToTransparentTopToBottom" | "colorToTransparentBottomToTop";
  blur?: number | "softest" | "soft" | "medium" | "strong" | "stronger" | "strongest" | "none";
  transition?: Transition;
  scale?: number;
  duration?: number;
  sides?: ("top" | "bottom" | "left" | "right")[];
};
export function GlowEffect({
  className,
  style,
  colors = ["#FF5733", "#33FF57", "#3357FF", "#F1C40F"],
  mode = "rotate",
  blur = "medium",
  transition,
  scale = 1,
  duration = 5,
  sides = ["top", "bottom", "left", "right"]
}: GlowEffectProps) {
  const BASE_TRANSITION = {
    repeat: Infinity,
    duration: duration,
    ease: "linear"
  };
  const animations = {
    rotate: {
      background: [`conic-gradient(from 0deg at 50% 50%, ${colors.join(", ")})`, `conic-gradient(from 360deg at 50% 50%, ${colors.join(", ")})`],
      transition: {
        ...(transition ?? BASE_TRANSITION)
      }
    },
    pulse: {
      background: colors.map(color => `radial-gradient(circle at 50% 50%, ${color} 0%, transparent 100%)`),
      scale: [1 * scale, 1.1 * scale, 1 * scale],
      opacity: [0.5, 0.8, 0.5],
      transition: {
        ...(transition ?? {
          ...BASE_TRANSITION,
          repeatType: "mirror"
        })
      }
    },
    breathe: {
      background: [...colors.map(color => `radial-gradient(circle at 50% 50%, ${color} 0%, transparent 100%)`)],
      scale: [1 * scale, 1.05 * scale, 1 * scale],
      transition: {
        ...(transition ?? {
          ...BASE_TRANSITION,
          repeatType: "mirror"
        })
      }
    },
    colorShift: {
      background: colors.map((color, index) => {
        const nextColor = colors[(index + 1) % colors.length];
        return `conic-gradient(from 0deg at 50% 50%, ${color} 0%, ${nextColor} 50%, ${color} 100%)`;
      }),
      transition: {
        ...(transition ?? {
          ...BASE_TRANSITION,
          repeatType: "mirror"
        })
      }
    },
    flowHorizontal: {
      background: colors.map(color => {
        const nextColor = colors[(colors.indexOf(color) + 1) % colors.length];
        return `linear-gradient(to right, ${color}, ${nextColor})`;
      }),
      transition: {
        ...(transition ?? {
          ...BASE_TRANSITION,
          repeatType: "mirror"
        })
      }
    },
    static: {
      background: `linear-gradient(to right, ${colors.join(", ")})`
    },
    colorToTransparentShift: {
      background: colors.map(color => {
        return `conic-gradient(from 0deg at 50% 50%, ${color} 0%, transparent 100%)`;
      }),
      transition: {
        ...(transition ?? {
          ...BASE_TRANSITION,
          repeatType: "mirror"
        })
      }
    },
    colorToTransparentLeftToRight: {
      background: colors.map(color => {
        return `linear-gradient(to right, ${color}, transparent)`;
      }),
      transition: {
        ...(transition ?? {
          ...BASE_TRANSITION,
          repeatType: "mirror"
        })
      }
    },
    colorToTransparentRightToLeft: {
      background: colors.map(color => {
        return `linear-gradient(to left, ${color}, transparent)`;
      }),
      transition: {
        ...(transition ?? {
          ...BASE_TRANSITION,
          repeatType: "mirror"
        })
      }
    },
    colorToTransparentTopToBottom: {
      background: colors.map(color => {
        return `linear-gradient(to bottom, ${color}, transparent)`;
      }),
      transition: {
        ...(transition ?? {
          ...BASE_TRANSITION,
          repeatType: "mirror"
        })
      }
    },
    colorToTransparentBottomToTop: {
      background: colors.map(color => {
        return `linear-gradient(to top, ${color}, transparent)`;
      }),
      transition: {
        ...(transition ?? {
          ...BASE_TRANSITION,
          repeatType: "mirror"
        })
      }
    }
  };
  const getBlurClass = (blur: GlowEffectProps["blur"]) => {
    if (typeof blur === "number") {
      return `blur-[${blur}px]`;
    }
    const presets = {
      softest: "blur-sm",
      soft: "blur",
      medium: "blur-md",
      strong: "blur-lg",
      stronger: "blur-xl",
      strongest: "blur-xl",
      none: "blur-none"
    };
    return presets[blur as keyof typeof presets];
  };
  const getSideClasses = (sides: string[]) => {
    // If all sides are selected, use the default full coverage
    const allSides = ["top", "bottom", "left", "right"];
    if (sides.length === 4 && allSides.every(side => sides.includes(side))) {
      return "inset-0 h-full w-full";
    }
    let classes = "absolute ";

    // Handle individual sides
    if (sides.includes("top")) {
      classes += "top-0 ";
    }
    if (sides.includes("bottom")) {
      classes += "bottom-0 ";
    }
    if (sides.includes("left")) {
      classes += "left-0 ";
    }
    if (sides.includes("right")) {
      classes += "right-0 ";
    }

    // Handle dimensions based on which sides are selected
    const hasHorizontal = sides.includes("left") || sides.includes("right");
    const hasVertical = sides.includes("top") || sides.includes("bottom");
    const hasTop = sides.includes("top");
    const hasBottom = sides.includes("bottom");
    const hasLeft = sides.includes("left");
    const hasRight = sides.includes("right");
    if (hasVertical && hasHorizontal) {
      // Corner or multiple sides - use full dimensions but position accordingly
      if (!hasTop) classes += "top-auto ";
      if (!hasBottom) classes += "bottom-auto ";
      if (!hasLeft) classes += "left-auto ";
      if (!hasRight) classes += "right-auto ";
      classes += "h-full w-full";
    } else if (hasVertical) {
      // Only top or bottom
      classes += "left-0 right-0 h-1 w-full";
    } else if (hasHorizontal) {
      // Only left or right
      classes += "top-0 bottom-0 w-1 h-full";
    }
    return classes;
  };
  return <motion.div style={{
    ...style,
    "--scale": scale,
    willChange: "transform",
    backfaceVisibility: "hidden"
  } as React.CSSProperties} animate={animations[mode]} className={cn("pointer-events-none", getSideClasses(sides), "scale-[var(--scale)] transform-gpu", getBlurClass(blur), className)} data-sentry-element="unknown" data-sentry-component="GlowEffect" data-sentry-source-file="glow-effect.tsx" />;
}